import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

const CLAVE = 'muni:return-url';
// Un destino viejo no debe secuestrar un login posterior: a los 15 minutos se descarta.
const VIGENCIA_MS = 15 * 60 * 1000;

const esRutaDeLogin = (url: string): boolean => /^\/login(\/|$)/.test(url.split(/[?#]/)[0]);

// Solo rutas internas de la app: nada de "//host" ni URLs absolutas, que servirían para
// mandar al usuario fuera del sitio después de iniciar sesión.
const esInterna = (url: string): boolean => url.startsWith('/') && !url.startsWith('//');

// Recuerda en qué vista estaba el usuario antes de ir al login, para devolverlo ahí cuando
// termine (código por correo, registro o Google). Con Google hay un viaje fuera de la app y
// la página se recarga, así que el destino se guarda en sessionStorage, no solo en memoria.
@Injectable({ providedIn: 'root' })
export class ReturnUrlService {
  private readonly router = inject(Router);
  // Última vista que no es del login. El componente del login se construye durante la
  // navegación, antes de que su propio NavigationEnd dispare, así que en ese momento esto
  // todavía apunta a la pantalla desde la que se entró.
  private ultimaVista: string | null = null;

  constructor() {
    this.router.events
      .pipe(
        filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(({ urlAfterRedirects }) => {
        if (!esRutaDeLogin(urlAfterRedirects)) {
          this.ultimaVista = urlAfterRedirects;
        }
      });
  }

  // La llama el login al abrirse: se queda con ?returnUrl=... si viene, y si no con la
  // pantalla desde la que se entró.
  capturar(explicito?: string | null): void {
    const destino = [explicito, this.ultimaVista].find(
      (url): url is string => !!url && esInterna(url) && !esRutaDeLogin(url),
    );
    if (!destino) {
      return;
    }
    try {
      sessionStorage.setItem(CLAVE, JSON.stringify({ url: destino, ts: Date.now() }));
    } catch {
      // Navegación privada o storage bloqueado: sin destino guardado se vuelve al inicio.
    }
  }

  // Devuelve el destino pendiente (y lo olvida). Null si no hay, si venció o si no es válido.
  consumir(): string | null {
    let guardado: string | null = null;
    try {
      guardado = sessionStorage.getItem(CLAVE);
      sessionStorage.removeItem(CLAVE);
    } catch {
      return null;
    }
    if (!guardado) {
      return null;
    }
    try {
      const { url, ts } = JSON.parse(guardado) as { url?: unknown; ts?: unknown };
      const vigente = typeof ts === 'number' && Date.now() - ts < VIGENCIA_MS;
      return typeof url === 'string' && esInterna(url) && !esRutaDeLogin(url) && vigente ? url : null;
    } catch {
      return null;
    }
  }
}
